import { useCallback, useMemo, useState } from 'react';
import { Theme, useMediaQuery, Chip, Box } from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid-pro';
import { Field, useFormikContext } from 'formik';
import classNames from 'classnames/bind';
import { getApiErrorMessage } from '@api/getApiError';
import { customersSlice, useLazyFetchCustomersQuery } from '@api/Trinium/Endpoints/Customer/slice';
import { Customer } from '@api/Trinium/Endpoints/Customer/types';
import { customerColumnDefinitions, mobileCustomerColumnDefinition } from '@components/DataGrid/Customer/columns';
import FilterableDataGrid from '@components/DataGrid/Generic/FilterableDataGrid';
import { FormikButtonDropdown } from '@forms/Components/Dropdowns/ButtonDropdown';
import { FormikAutocomplete } from '@forms/Components/AutocompleteInput';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import useDebouncer from '@hooks/useDebounce';
import { store } from '@reduxStore';

/** We want the grid style sheet since we are rendering a grid for the input */
import styles from '../Components/DataGrid/RenderCell.module.css';
import btnStyles from '../Forms/Components/Dropdowns/ButtonDropdown.module.css';
const styleContext = classNames.bind({ ...styles, ...btnStyles });

interface CustomerFieldProps {
	fieldName: string;
	label?: string;
	className?: string;
	buttonClassName?: string;
	disabled?: boolean;
	required?: boolean;
	multiple?: boolean;
}

/**
 * Form Component that renders a Button Dropdown implementation designed for selecting a Customer
 * using a Data Grid Pro in the Button Dropdown Popup Component.
 */
export default function CustomerField(props: CustomerFieldProps) {
	const { getFieldMeta, setFieldValue } = useFormikContext();
	const { value } = getFieldMeta<Customer | Customer[]>(props.fieldName);
	const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

	const columns = useMemo(() => (!isMobile ? customerColumnDefinitions : mobileCustomerColumnDefinition), [isMobile]);

	const onGridRowSelect = useCallback(
		({ row }: GridRowParams<Customer>) => {
			if (props.multiple) {
				const currentValue = Array.isArray(value) ? value : [];
				const exists = currentValue.some((c) => c.id === row.id);

				if (exists) {
					// Remove customer if already selected
					void setFieldValue(
						props.fieldName,
						currentValue.filter((c) => c.id !== row.id)
					);
				} else {
					// Add customer to selection
					void setFieldValue(props.fieldName, [...currentValue, row]);
				}
			} else {
				void setFieldValue(props.fieldName, row);
			}
		},
		[setFieldValue, props.fieldName, props.multiple, value]
	);

	const handleDeleteCustomer = useCallback(
		(customerId: string) => {
			if (props.multiple && Array.isArray(value)) {
				void setFieldValue(
					props.fieldName,
					value.filter((c) => c.id !== customerId)
				);
			}
		},
		[setFieldValue, props.fieldName, props.multiple, value]
	);

	const getRowClassName = useCallback((row: Customer) => {
		if (row.hasCreditWrn) return styles['credit-warning'];
		return undefined;
	}, []);

	return (
		<>
			<div
				className={styleContext({
					['field-container']: true,
					[props.className ?? '']: Boolean(props.className)
				})}
				style={{ display: 'flex', flexDirection: 'row' }}
			>
				<Field
					name={props.fieldName}
					label={props.label}
					component={FormikButtonDropdown({
						ButtonContent: CustomerButtonContent,
						buttonContentProps: {
							customer: value,
							multiple: props.multiple
						},
						buttonConfig: {
							className: props.buttonClassName
						},
						PopupContent: FilterableDataGrid,
						popupContentProps: {
							columnDefinitions: columns,
							onFilterCallback: onCustomerSearch,
							onRowSelect: onGridRowSelect,
							getRowClassName: getRowClassName,
							checkboxSelection: props.multiple,
							rowSelectionModel: props.multiple ? (Array.isArray(value) ? value : []).map((c) => c.id) : undefined
						}
					})}
					width='800px'
					height='280px'
					disabled={props.disabled}
					required={props.required}
					clearable
				/>
			</div>
			{props.multiple && Array.isArray(value) && value.length > 0 && (
				<div>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
						{value.map((customer) => (
							<Chip
								key={customer.id}
								label={`${customer.name} `}
								onDelete={() => {
									handleDeleteCustomer(customer.id);
								}}
								disabled={props.disabled}
							/>
						))}
					</Box>
				</div>
			)}
		</>
	);
}

/** Button Display */
function CustomerButtonContent({ customer, multiple }: { customer?: Customer | Customer[] | null; multiple?: boolean }) {
	if (!customer) return 'Select a Customer';

	if (multiple && Array.isArray(customer)) {
		if (customer.length === 0) return 'Select Customers';
		return `${String(customer.length)} customer${customer.length !== 1 ? 's' : ''} selected`;
	}

	const singleCustomer = customer as Customer;
	return <>{`${singleCustomer.name} (${singleCustomer.id})`}</>;
}

/** Callback when the Quick Filter value changes */
const onCustomerSearch = (searchValue: string) => {
	const terminalNumber = store.getState().applicationState.selectedTerminal?.number;

	if (!terminalNumber) throw new Error('Could not resolve a selected terminal for the application.');

	return new Promise<Customer[]>((resolve, reject) => {
		store
			.dispatch(
				customersSlice.endpoints.fetchCustomers.initiate({
					terminalNumber: terminalNumber,
					startsWith: searchValue
				})
			)
			.unwrap()
			.then((response) => {
				resolve(response);
			})
			.catch((error: unknown) => {
				const deserializedError = getApiErrorMessage(error);
				reject(new Error(deserializedError));
			});
	});
};

export function CustomerAutocompleteField({ fieldName, className, multiple }: CustomerFieldProps) {
	const { number: terminalNumber } = useCurrentTerminal();
	const { getFieldMeta } = useFormikContext();
	const { value } = getFieldMeta<string | string[]>(fieldName);

	const [fetchCustomers, { customerNames, fetchingCustomers }] = useLazyFetchCustomersQuery({
		selectFromResult: ({ data, isFetching }) => ({
			customerNames: data
				? data.map((customer) => `(${customer.id}) ${customer.name}`)
				: value
					? Array.isArray(value)
						? value
						: [value]
					: [],
			fetchingCustomers: isFetching
		})
	});

	const [debouncedCustomerValue, setDebouncedCustomerValue] = useState<string | null>(null);
	useDebouncer(
		() => {
			if (debouncedCustomerValue && debouncedCustomerValue.length > 2) {
				void fetchCustomers({
					terminalNumber,
					startsWith: debouncedCustomerValue
				});
			}
		},
		[debouncedCustomerValue, terminalNumber, fetchCustomers],
		500
	);

	const onChangeExtension = useCallback(
		(value: string | null) => {
			setDebouncedCustomerValue(value);
		},
		[setDebouncedCustomerValue]
	);

	return (
		<Field
			name={fieldName}
			label='Customer'
			placeholder='Enter Customer Name'
			component={FormikAutocomplete}
			variant='standard'
			autoCompleteItems={customerNames}
			isLoading={fetchingCustomers}
			className={className}
			onChangeExtension={onChangeExtension}
			multiple={multiple}
		/>
	);
}
